/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Wed May 03 2023
 **/

import { Button, Typography, useModal, useToast } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { useGroup, useSpace } from "@/frameworks/core/API";

import * as S from "./emotion";
import { ModalID } from "@/frameworks/core/commons";
import { EventHandler } from "@rfice/eventhandler";
import { EventID } from "@/frameworks/core/commons";
import React from "react";

export default function DeleteGroup({ name, groupID }: { name: string; groupID: string }) {
	const modal = useModal();
	const toast = useToast();
	const spaceAPI = useSpace({
		onSuccess() {
			modal.hide(ModalID.DELETE_GROUP);
		}
	});
	const groupAPI = useGroup();

	const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		try {
			await groupAPI.delete.execute({ groupId: groupID });
			await spaceAPI.mySpaces.list.execute();
			// Group delete에 대한 stomp 메세지가 없어서 삭제한 그룹의 스페이스 아이디를 event emit으로 넘김
			const deletedGroup = spaceAPI.mySpaces.list.result.data?.groups.filter((item) => item.id === groupID)[0];
			if (!deletedGroup) {
				throw new Error("deleted group not found");
			}
			const deletedSpaceChatIds = deletedGroup.spaces.map((item) => item.spaceId);

			EventHandler.emit(EventID.onGroupCommand, "delete", { groupID, spaceIDs: deletedSpaceChatIds });
			toast.show({ type: "success", message: _T("삭제 되었습니다.") });
		} catch (_error) {
			const error = _error as Error;
			toast.show({ type: "error", message: error.message });
		}
	};

	const handleCancel = () => {
		modal.hide(ModalID.DELETE_GROUP);
	};

	return (
		<S.Wrapper gap={10} horizonAlign="left">
			<S.ContentWrapper>
				<Typography typoSize="Title1">
					{_T("{{spaceName}} 그룹을 삭제하시겠습니까?", { spaceName: name })}
				</Typography>
			</S.ContentWrapper>
			<S.Footer horizonAlign="center" verticalAlign="center">
				<Button.Group>
					<Button filled="secondary" css={{ minWidth: "120px" }} onClick={handleCancel}>
						{_T("취소")}
					</Button>
					<Button css={{ minWidth: "120px" }} onClick={handleDelete}>
						{_T("확인")}
					</Button>
				</Button.Group>
			</S.Footer>
		</S.Wrapper>
	);
}
